import React, { FC, useState, useCallback, useEffect, useRef } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Checkbox from '@mui/material/Checkbox'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import TablePagination from '@mui/material/TablePagination'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import FormControlLabel from '@mui/material/FormControlLabel'
import TableSortLabel from '@mui/material/TableSortLabel'
import { useHistory } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  fetchAdminHosts,
  bulkToggleVisibility,
  fetchUiConfig,
  updateUiConfig,
  StrAdminHost,
  UiConfig,
} from 'services/strAdminApi'

const PAGE_SIZE = 50

const STRAdminPage: FC = () => {
  const history = useHistory()
  const queryClient = useQueryClient()
  const [hosts, setHosts] = useState<StrAdminHost[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [searchValue, setSearchValue] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [selectedHostIds, setSelectedHostIds] = useState<Set<string>>(new Set())
  const [pendingChanges, setPendingChanges] = useState<Map<string, boolean>>(new Map())
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Sort state for Visible column
  const [sortByVisible, setSortByVisible] = useState<'asc' | 'desc' | null>(null)

  // UI Config state
  const [uiConfig, setUiConfig] = useState<UiConfig | null>(null)
  const [uiConfigLoading, setUiConfigLoading] = useState(false)
  const [uiConfigSaving, setUiConfigSaving] = useState(false)
  const [uiConfigSuccess, setUiConfigSuccess] = useState('')
  const [uiConfigError, setUiConfigError] = useState('')

  const buildSorting = useCallback(() => {
    if (!sortByVisible) return undefined
    return { id: 'isVisible', desc: sortByVisible === 'desc' }
  }, [sortByVisible])

  const loadHosts = useCallback(async (pageNum: number, search: string, sorting?: { id: string; desc: boolean }) => {
    setLoading(true)
    setError('')
    try {
      const result = await fetchAdminHosts(pageNum * PAGE_SIZE, PAGE_SIZE, search || undefined, sorting)
      setHosts(result.hosts)
      setTotal(result.total)
    } catch (err) {
      setError('Failed to load hosts')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadHosts(page, searchValue, buildSorting())
  }, [page, searchValue, sortByVisible, loadHosts, buildSorting])

  // Load UI config on mount
  useEffect(() => {
    const loadConfig = async () => {
      setUiConfigLoading(true)
      try {
        const config = await fetchUiConfig()
        setUiConfig(config)
      } catch {
        setUiConfigError('Failed to load UI config')
      } finally {
        setUiConfigLoading(false)
      }
    }
    loadConfig()
  }, [])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setSearchInput(val)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setSearchValue(val)
      setPage(0)
    }, 400)
  }

  const handleToggleVisibility = (host: StrAdminHost) => {
    const currentVisible = pendingChanges.has(host.hostId)
      ? pendingChanges.get(host.hostId)!
      : host.isVisible
    const newVisible = !currentVisible

    setPendingChanges(prev => {
      const next = new Map(prev)
      if (newVisible === host.isVisible) {
        next.delete(host.hostId)
      } else {
        next.set(host.hostId, newVisible)
      }
      return next
    })
    setSuccess('')
  }

  const handleUpdateNow = async () => {
    if (pendingChanges.size === 0) return
    setSaving(true)
    setError('')
    setSuccess('')
    try {
      const toShow = Array.from(pendingChanges.entries()).filter(([, v]) => v).map(([id]) => id)
      const toHide = Array.from(pendingChanges.entries()).filter(([, v]) => !v).map(([id]) => id)

      if (toShow.length > 0) await bulkToggleVisibility(toShow, true)
      if (toHide.length > 0) await bulkToggleVisibility(toHide, false)

      setHosts(prev =>
        prev.map(h => {
          if (pendingChanges.has(h.hostId)) {
            return { ...h, isVisible: pendingChanges.get(h.hostId)! }
          }
          return h
        })
      )
      setSuccess(`Updated ${pendingChanges.size} host(s)`)
      setPendingChanges(new Map())
    } catch {
      setError('Failed to update host visibility')
    } finally {
      setSaving(false)
    }
  }

  const handleSelectHost = (hostId: string) => {
    setSelectedHostIds(prev => {
      const next = new Set(prev)
      if (next.has(hostId)) next.delete(hostId)
      else next.add(hostId)
      return next
    })
  }

  const handleSelectAll = () => {
    if (selectedHostIds.size === hosts.length) {
      setSelectedHostIds(new Set())
    } else {
      setSelectedHostIds(new Set(hosts.map(h => h.hostId)))
    }
  }

  const handleBulkAction = (isVisible: boolean) => {
    setPendingChanges(prev => {
      const next = new Map(prev)
      for (const hostId of selectedHostIds) {
        const host = hosts.find(h => h.hostId === hostId)
        if (host) {
          if (isVisible === host.isVisible) {
            next.delete(hostId)
          } else {
            next.set(hostId, isVisible)
          }
        }
      }
      return next
    })
    setSelectedHostIds(new Set())
    setSuccess('')
  }

  const handleToggleSort = () => {
    setSortByVisible(prev => {
      if (prev === null) return 'desc'
      if (prev === 'desc') return 'asc'
      return null
    })
    setPage(0)
  }

  const getEffectiveVisibility = (host: StrAdminHost) =>
    pendingChanges.has(host.hostId) ? pendingChanges.get(host.hostId)! : host.isVisible

  // UI Config handlers
  const handleUiConfigToggle = (section: 'hosts' | 'listings', key: string, value: boolean) => {
    if (!uiConfig) return
    setUiConfig({
      ...uiConfig,
      overviewBar: {
        ...uiConfig.overviewBar,
        [section]: {
          ...uiConfig.overviewBar[section],
          [key]: value,
        },
      },
    })
    setUiConfigSuccess('')
  }

  const handleSaveUiConfig = async () => {
    if (!uiConfig) return
    setUiConfigSaving(true)
    setUiConfigError('')
    setUiConfigSuccess('')
    try {
      const updated = await updateUiConfig({ overviewBar: uiConfig.overviewBar })
      setUiConfig(updated)
      setUiConfigSuccess('UI configuration saved')
      queryClient.invalidateQueries({ queryKey: ['str-ui-config'] })
    } catch {
      setUiConfigError('Failed to save UI config')
    } finally {
      setUiConfigSaving(false)
    }
  }

  const statLabels: Record<string, string> = {
    hostsCount: 'Number of hosts',
    listingsCount: 'Number of listings',
    documentedRevenue: 'Documented Revenue',
    reportedRevenue: 'Reported Revenue',
    taxGap: 'Tax Gap',
  }

  return (
    <Box sx={{ p: 3, fontFamily: 'Source Sans Pro' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant='h5' sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
          STR Host Management
        </Typography>
        <Button variant='outlined' size='small' onClick={() => {
          queryClient.invalidateQueries({ queryKey: ['str-entities-key'] })
          queryClient.invalidateQueries({ queryKey: ['str-overview-key'] })
          history.push('/short-term-rentals')
        }}>
          Back to STR Table
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField
          size='small'
          placeholder='Search by name...'
          value={searchInput}
          onChange={handleSearchChange}
          sx={{ width: 300 }}
        />
        <Button
          variant='contained'
          size='small'
          color='primary'
          disabled={pendingChanges.size === 0 || saving}
          onClick={handleUpdateNow}
          startIcon={saving ? <CircularProgress size={16} color='inherit' /> : undefined}
        >
          {saving ? 'Saving...' : `Update Now (${pendingChanges.size})`}
        </Button>
        {selectedHostIds.size > 0 && (
          <>
            <Typography variant='body2' sx={{ alignSelf: 'center' }}>
              {selectedHostIds.size} selected
            </Typography>
            <Button variant='contained' size='small' color='success' onClick={() => handleBulkAction(true)}>
              Show Selected
            </Button>
            <Button variant='contained' size='small' color='warning' onClick={() => handleBulkAction(false)}>
              Hide Selected
            </Button>
          </>
        )}
      </Box>

      {error && <Alert severity='error' sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity='success' sx={{ mb: 2 }}>{success}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper variant='outlined'>
          <TableContainer>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell padding='checkbox'>
                    <Checkbox
                      indeterminate={selectedHostIds.size > 0 && selectedHostIds.size < hosts.length}
                      checked={hosts.length > 0 && selectedHostIds.size === hosts.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortByVisible !== null}
                      direction={sortByVisible || 'desc'}
                      onClick={handleToggleSort}
                    >
                      Visible
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Host Name</TableCell>
                  <TableCell sx={{ textTransform: 'none' }}>Verified Name</TableCell>
                  <TableCell>RUC</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell align='right'>Listings</TableCell>
                  <TableCell align='right'>Reviews</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {hosts.map(host => {
                  const effectiveVisible = getEffectiveVisibility(host)
                  const hasChange = pendingChanges.has(host.hostId)
                  return (
                    <TableRow
                      key={host.hostId}
                      hover
                      sx={hasChange ? { backgroundColor: 'rgba(25, 118, 210, 0.06)' } : undefined}
                    >
                      <TableCell padding='checkbox'>
                        <Checkbox
                          checked={selectedHostIds.has(host.hostId)}
                          onChange={() => handleSelectHost(host.hostId)}
                        />
                      </TableCell>
                      <TableCell>
                        <Checkbox
                          checked={effectiveVisible}
                          onChange={() => handleToggleVisibility(host)}
                          color='primary'
                        />
                      </TableCell>
                      <TableCell>{host.totals?.hostName || '-'}</TableCell>
                      <TableCell>{host.entityDetails?.name || '-'}</TableCell>
                      <TableCell>{host.entityDetails?.tin || '-'}</TableCell>
                      <TableCell>{host.entityDetails?.location || '-'}</TableCell>
                      <TableCell align='right'>{host.totals?.totalListings ?? 0}</TableCell>
                      <TableCell align='right'>{host.totals?.numberOfReviews ?? 0}</TableCell>
                    </TableRow>
                  )
                })}
                {hosts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align='center' sx={{ py: 4 }}>
                      No hosts found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component='div'
            count={total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
          />
        </Paper>
      )}

      {/* UI Configuration Section — only shown when ENABLE_STR_UI_CONFIG=true */}
      {uiConfig?.uiConfigEnabled && (
      <Box sx={{ mt: 4 }}>
        <Typography variant='h6' sx={{ fontWeight: 700, textTransform: 'uppercase', mb: 2 }}>
          UI Configuration
        </Typography>

        {uiConfigError && <Alert severity='error' sx={{ mb: 2 }}>{uiConfigError}</Alert>}
        {uiConfigSuccess && <Alert severity='success' sx={{ mb: 2 }}>{uiConfigSuccess}</Alert>}

        {uiConfigLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : uiConfig ? (
          <>
            {(['hosts', 'listings'] as const).map(section => (
              <Accordion key={section} defaultExpanded>
                <AccordionSummary expandIcon={<span style={{ fontSize: '1.2rem' }}>&#9660;</span>}>
                  <Typography sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                    Overview Bar &mdash; {section}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {Object.entries(uiConfig.overviewBar[section]).map(([key, val]) => (
                      <FormControlLabel
                        key={key}
                        control={
                          <Checkbox
                            checked={val as boolean}
                            onChange={(_, checked) => handleUiConfigToggle(section, key, checked)}
                          />
                        }
                        label={statLabels[key] || key}
                      />
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
            <Box sx={{ mt: 2 }}>
              <Button
                variant='contained'
                color='primary'
                disabled={uiConfigSaving}
                onClick={handleSaveUiConfig}
                startIcon={uiConfigSaving ? <CircularProgress size={16} color='inherit' /> : undefined}
              >
                {uiConfigSaving ? 'Saving...' : 'Save UI Config'}
              </Button>
            </Box>
          </>
        ) : null}
      </Box>
      )}
    </Box>
  )
}

export default STRAdminPage
